import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import { getActiveLiveTvChannels, huayYoutubeLiveToChannels, type LiveTvChannel } from '../lib/liveTv'
import { parseYoutubeVideoId, toYoutubeEmbedUrl, toYoutubeThumbnailUrl } from '../lib/youtubeUrl'
import { lotteryService } from '../services/lotteryService'
import './ModernLiveTvPage.css'

interface ModernLiveTvPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: {
    CDN_BASE_URL: string
    CSS_PATH: string
    IMG_PATH: string
    FONTS_PATH: string
  }
}

type FilterKey = 'all' | 'football' | 'lottery' | 'other'

const ModernLiveTvPage: React.FC<ModernLiveTvPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const [huayNameByKey, setHuayNameByKey] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const map: Record<string, string> = {}

      try {
        const sharedMap = await lotteryService.getSharedHuayNameMap()
        if (cancelled) return
        for (const [k, v] of Object.entries(sharedMap)) {
          if (v && !map[k]) map[k] = v
        }
      } catch {
        // ignore
      }

      try {
        const rounds = await lotteryService.getTodayRounds()
        if (cancelled) return
        for (const round of rounds) {
          const name = (round as { huayName?: string }).huayName?.trim()
          if (!name) continue
          const code = (round as { huayCode?: string }).huayCode
          const huayId = (round as { huayId?: string }).huayId
          const id = (round as { _id?: string })._id
          if (code) {
            const k = String(code).trim().toUpperCase()
            if (k && !map[k]) map[k] = name
          }
          if (huayId) {
            const k = String(huayId).trim()
            if (k && !map[k]) map[k] = name
          }
          if (id) {
            const k = String(id).trim()
            if (k && !map[k]) map[k] = name
          }
        }
      } catch {
        // ignore
      }

      if (!cancelled) setHuayNameByKey(map)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const channels = useMemo<LiveTvChannel[]>(() => {
    if (themeConfig.settings.liveTvEnabled !== true) return []
    const football = getActiveLiveTvChannels(themeConfig.settings.liveTvChannels || [])
    const lottery = huayYoutubeLiveToChannels(themeConfig.settings.huayYoutubeLive || null, huayNameByKey)
    return [...football, ...lottery]
  }, [themeConfig.settings.liveTvEnabled, themeConfig.settings.liveTvChannels, themeConfig.settings.huayYoutubeLive, huayNameByKey])

  const [filter, setFilter] = useState<FilterKey>('all')
  const [selectedId, setSelectedId] = useState<string | null>(channels[0]?.id || null)

  useEffect(() => {
    if (!channels.length) {
      setSelectedId(null)
      return
    }
    setSelectedId((prev) => {
      if (prev && channels.some((c) => c.id === prev)) return prev
      return channels[0]?.id ?? null
    })
  }, [channels])

  const counts = useMemo(() => {
    const out = { football: 0, lottery: 0, other: 0 }
    for (const c of channels) {
      const cat = c.category ?? 'other'
      if (cat === 'football') out.football += 1
      else if (cat === 'lottery') out.lottery += 1
      else out.other += 1
    }
    return out
  }, [channels])

  const filtered = useMemo(() => {
    if (filter === 'all') return channels
    return channels.filter((c) => (c.category ?? 'other') === filter)
  }, [channels, filter])

  const playingChannel = useMemo(() => {
    if (!selectedId) return channels[0] ?? null
    return channels.find((c) => c.id === selectedId) ?? channels[0] ?? null
  }, [channels, selectedId])

  const videoId = playingChannel ? parseYoutubeVideoId(playingChannel.youtubeUrl) : null
  const playerStartMuted = themeConfig.settings.liveTvPlayerStartMuted !== false

  const renderChip = (key: FilterKey, label: string, count: number) => {
    if (key !== 'all' && count === 0) return null
    const active = filter === key
    return (
      <button
        key={key}
        type="button"
        onClick={() => setFilter(key)}
        className={`htd-livetv__chip${active ? ' is-active' : ''}`}
      >
        {label}
        <span className="htd-livetv__chip-count">{count}</span>
      </button>
    )
  }

  return (
    <div className="content htd-livetv">
      <div className="htd-livetv__glow" aria-hidden />
      <div className="htd-livetv__inner">
        <header className="htd-livetv__hero htd-livetv-anim htd-livetv-anim--1">
          <div className="htd-livetv__hero-main">
            <div className="htd-livetv__badge" aria-hidden>
              <i className="fas fa-tv" />
            </div>
            <div>
              <div className="htd-livetv__eyebrow-row">
                <span className="htd-livetv__live-pill">
                  <span className="htd-livetv__live-dot" aria-hidden />
                  LIVE
                </span>
                <span className="htd-livetv__count">ทั้งหมด {channels.length} ช่อง</span>
              </div>
              <h1 className="htd-livetv__title">ดูถ่ายทอดสด</h1>
              <p className="htd-livetv__desc">ช่องบอล / กีฬา / ถ่ายทอดสดหวย — อัปเดตโดยแอดมิน</p>
            </div>
          </div>
          <button type="button" className="htd-livetv__back" onClick={() => navigate('/dashboard')}>
            <i className="fas fa-arrow-left" aria-hidden />
            กลับศูนย์ควบคุม
          </button>
        </header>

        {channels.length === 0 ? (
          <div className="htd-livetv__empty htd-livetv-anim htd-livetv-anim--2">
            <i className="fas fa-tv" aria-hidden />
            <p>ยังไม่มีช่องถ่ายทอดสดในขณะนี้</p>
            <small>แอดมินยังไม่ได้เปิดช่องถ่ายทอดสด — กรุณาตรวจสอบในภายหลัง</small>
          </div>
        ) : (
          <>
            {playingChannel && videoId ? (
              <section className="htd-livetv__player htd-livetv-anim htd-livetv-anim--2" aria-label="เครื่องเล่นถ่ายทอดสด">
                <div className="htd-livetv__player-bar">
                  <div className="htd-livetv__player-title-wrap">
                    <span className="htd-livetv__live-pill">
                      <span className="htd-livetv__live-dot" aria-hidden />
                      LIVE
                    </span>
                    <span className="htd-livetv__player-title">{playingChannel.title}</span>
                    {playingChannel.category === 'lottery' ? (
                      <span className="htd-livetv__cat-chip htd-livetv__cat-chip--lottery">
                        <i className="fas fa-trophy" aria-hidden />
                        ถ่ายทอดสดหวย
                      </span>
                    ) : null}
                    {playingChannel.category === 'football' ? (
                      <span className="htd-livetv__cat-chip htd-livetv__cat-chip--football">
                        <i className="fas fa-futbol" aria-hidden />
                        บอล / กีฬา
                      </span>
                    ) : null}
                    {!playingChannel.category || playingChannel.category === 'other' ? (
                      <span className="htd-livetv__cat-chip htd-livetv__cat-chip--other">
                        <i className="fas fa-tv" aria-hidden />
                        อื่นๆ
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="ratio ratio-16x9 htd-livetv__iframe-wrap">
                  <iframe
                    key={`${playingChannel.id}-${playerStartMuted ? 'muted' : 'sound'}`}
                    title={playingChannel.title}
                    src={toYoutubeEmbedUrl(videoId, { autoplay: true, muted: playerStartMuted })}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </section>
            ) : null}

            <nav className="htd-livetv__filters htd-livetv-anim htd-livetv-anim--3" aria-label="กรองช่องถ่ายทอดสด">
              {renderChip('all', 'ทั้งหมด', channels.length)}
              {renderChip('football', 'บอล / กีฬา', counts.football)}
              {renderChip('lottery', 'ถ่ายทอดสดหวย', counts.lottery)}
              {renderChip('other', 'อื่นๆ', counts.other)}
            </nav>

            <div className="htd-livetv__grid htd-livetv-anim htd-livetv-anim--4">
              {filtered.map((channel) => {
                const cVid = parseYoutubeVideoId(channel.youtubeUrl)
                const isActive = selectedId === channel.id
                const isLottery = channel.category === 'lottery'
                const isFootball = channel.category === 'football'
                const catClass = isLottery
                  ? 'htd-livetv__cat-icon--lottery'
                  : isFootball
                    ? 'htd-livetv__cat-icon--football'
                    : 'htd-livetv__cat-icon--other'
                return (
                  <button
                    key={channel.id}
                    type="button"
                    onClick={() => setSelectedId(channel.id)}
                    className={`htd-livetv__card${isActive ? ' is-active' : ''}`}
                  >
                    <div className="htd-livetv__thumb">
                      {cVid ? (
                        <img
                          src={toYoutubeThumbnailUrl(cVid, 'mq')}
                          alt={channel.title}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : (
                        <div className="htd-livetv__thumb-fallback">
                          <i className="fas fa-tv" />
                        </div>
                      )}
                      {isActive ? <span className="htd-livetv__now">กำลังเล่น</span> : null}
                      <span className="htd-livetv__card-live">LIVE</span>
                    </div>
                    <div className="htd-livetv__meta">
                      <span className={`htd-livetv__cat-icon ${catClass}`} aria-hidden>
                        <i className={`fas ${isLottery ? 'fa-trophy' : isFootball ? 'fa-futbol' : 'fa-tv'}`} />
                      </span>
                      <h6 className="htd-livetv__card-title" title={channel.title}>
                        {channel.title}
                      </h6>
                    </div>
                  </button>
                )
              })}
              {filtered.length === 0 ? (
                <div className="htd-livetv__filter-empty">
                  <i className="fas fa-filter me-2" aria-hidden />
                  ไม่มีช่องในหมวดนี้ — ลองเลือกหมวดอื่น
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ModernLiveTvPage
